import React, { useEffect, useState } from 'react'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { api } from '../../../lib/api'

const FUND_USE_MAX = 1000
const BOTH_BLANK_ERROR = 'Add a description or a link. Without one, donations are turned off.'
const HTTPS_ERROR = 'Link must start with https://'

const ORG_TYPE_LABELS = {
  parish: 'Parish',
  catholic_center: 'Catholic center',
}

const VERIFICATION_LABELS = {
  unverified: 'Not yet submitted',
  pending: 'Pending review',
  verified: 'Verified',
  rejected: 'Rejected',
}

const MODE_EXPLANATIONS = {
  in_app: 'Donors give inside the app.',
  web: 'Donors give on the web until the organization completes verification for in-app giving.',
  unavailable: 'Donations are off.',
}

const UNAVAILABLE_EXPLANATIONS = {
  no_stripe_account: 'Donations are off: no payout account is connected yet.',
  charges_disabled: 'Donations are off: the payout account cannot accept charges yet.',
  inactive: 'Donations are off: the parish is not active.',
  missing_fund_use: 'Donations are off: add a description or a link below.',
}

export function givingModeText(giving) {
  giving = giving ?? {}
  return giving.mode === 'unavailable'
    ? UNAVAILABLE_EXPLANATIONS[giving.unavailableReason] || MODE_EXPLANATIONS.unavailable
    : MODE_EXPLANATIONS[giving.mode]
}

export function FundUseForm({ parish, onSaved }) {
  const [text, setText] = useState(parish.fundUseText || '')
  const [url, setUrl] = useState(parish.fundUseUrl || '')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setText(parish.fundUseText || '')
    setUrl(parish.fundUseUrl || '')
  }, [parish.id, parish.fundUseText, parish.fundUseUrl])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaved(false)
    const trimmedText = text.trim()
    const trimmedUrl = url.trim()
    if (!trimmedText && !trimmedUrl) {
      setError(BOTH_BLANK_ERROR)
      return
    }
    if (trimmedUrl && !trimmedUrl.startsWith('https://')) {
      setError(HTTPS_ERROR)
      return
    }
    setError('')
    setSaving(true)
    try {
      const updated = await api.updateParish(parish.id, {
        fundUseText: trimmedText || null,
        fundUseUrl: trimmedUrl || null,
      })
      setSaved(true)
      if (onSaved) onSaved(updated)
    } catch (err) {
      setError(err.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4 max-w-xl">
      <div>
        <Label htmlFor="fund-use-text" className="text-charcoal text-xs">How donations are used</Label>
        <textarea
          id="fund-use-text"
          name="fundUseText"
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            setSaved(false)
          }}
          rows={4}
          maxLength={FUND_USE_MAX}
          className="mt-1 w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal placeholder-charcoal-faint focus:outline-none focus:ring-2 focus:ring-gold/50"
          placeholder="Describe how donations support the parish."
        />
        <p className="mt-1 text-xs text-charcoal-muted">{text.length}/{FUND_USE_MAX}</p>
      </div>
      <div>
        <Label htmlFor="fund-use-url" className="text-charcoal text-xs">Link to more details</Label>
        <Input
          id="fund-use-url"
          name="fundUseUrl"
          type="url"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value)
            setSaved(false)
          }}
          placeholder="https://"
          className="mt-1 bg-white border-surface-border text-charcoal"
        />
      </div>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      {saved && <p className="text-sm text-charcoal-muted">Saved.</p>}
      <div className="flex justify-end">
        <Button type="submit" disabled={saving} className="bg-gold text-black hover:bg-gold/90">
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

function StatusRow({ label, value }) {
  return (
    <div className="flex gap-4 py-2 border-b border-surface-border text-sm">
      <dt className="w-40 shrink-0 text-charcoal-muted">{label}</dt>
      <dd className="text-charcoal">{value || 'Not provided'}</dd>
    </div>
  )
}

export default function ParishGivingSettings() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [parish, setParish] = useState(null)

  // PATCH /v1/parishes/:id does not return `giving` (only GET attaches it),
  // so re-read the parish after a save to refresh the mode row. If the
  // re-read fails, keep the values the save returned.
  const refreshAfterSave = async (updated) => {
    try {
      const fresh = await api.getParish(parish.id)
      setParish(fresh)
    } catch {
      setParish((prev) => ({ ...prev, ...updated }))
    }
  }

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const userData = await api.getMe()
        const id = (userData?.roleDetails || []).find((r) => r.name === 'parish-admin')?.parishId
        if (!id) {
          if (!cancelled) setError('You are not associated with a parish as a parish admin. Please contact support.')
          return
        }
        const parishData = await api.getParish(id)
        if (!cancelled) setParish(parishData)
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load parish data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) return <p className="text-sm text-charcoal-muted">Loading...</p>
  if (error) return <p role="alert" className="text-sm text-red-600">{error}</p>
  if (!parish) return null

  const giving = parish.giving || {}
  const modeText = givingModeText(giving)

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-serif text-charcoal">Giving</h1>

      <section>
        <h2 className="text-sm font-semibold text-charcoal mb-3">How donations are described</h2>
        <FundUseForm parish={parish} onSaved={refreshAfterSave} />
      </section>

      <section>
        <h2 className="text-sm font-semibold text-charcoal mb-3">Organization status</h2>
        <dl>
          <StatusRow label="Organization type" value={ORG_TYPE_LABELS[parish.orgType]} />
          <StatusRow label="Legal name" value={parish.legalName} />
          <StatusRow label="EIN" value={parish.ein} />
          <StatusRow label="Review status" value={VERIFICATION_LABELS[parish.verificationStatus]} />
          <StatusRow label="Giving mode" value={modeText} />
        </dl>
      </section>
    </div>
  )
}
